import Stack from "@mui/material/Stack";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  CircularProgress,
  Grid,
  TextField,
  useMediaQuery,
} from "@mui/material";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import moment from "moment";
import { updateEntryBook } from "app/redux/slice/entrybookSlice";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

const EditEntryBook = ({
  value,
  onClose,
  getData,
  expenseTypeData,
  incomeTypeData,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const mobileView = useMediaQuery("(max-width:575px)");

  const validationSchema = Yup.object({
    amount: Yup.number()
      .typeError("Amount must be a number.")
      .required("Please enter amount.")
      .min(1, "Amount must be greater than zero."),
    transactionDate: Yup.date().required("Please select transaction date."),
    note: Yup.string().required("Please enter note."),
    expenseType: Yup.mixed().when("paymentOf", {
      is: "expense",
      then: (schema) => schema.required("Please select an expense type."),
      otherwise: (schema) => schema.nullable(),
    }),
    incomeType: Yup.mixed().when("paymentOf", {
      is: "party",
      then: (schema) => schema.required("Please select an income type."),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  return (
    <>
      <Formik
        initialValues={{
          bookType: value?.bookType,
          transactionType: value?.transactionType,
          paymentOf: value?.paymentOf,
          vendorId: value?.vendorId,
          platformId: value?.platformId,
          invoiceId: value?.invoiceId,
          billId: value?.billId,
          expenseType: value?.expenseTypeId,
          incomeType: value?.incomeTypeId,
          transactionDate: moment(value?.transactionDate).format("YYYY-MM-DD"),
          amount:
            value?.transactionType === "credit"
              ? value?.creditAmount
              : value?.debitAmount,
          note: value?.note,
        }}
        validationSchema={validationSchema}
        onSubmit={async (data, { setSubmitting, resetForm }) => {
          setSubmitting(true);
          let finalData = {
            bookType: data?.bookType,
            transactionType: data?.transactionType,
            paymentOf: data?.paymentOf,
            transactionDate: data?.transactionDate,
            note: data?.note,
          };
          if (data?.paymentOf === "party") {
            finalData.incomeTypeId = data?.incomeType?._id;
            finalData.vendorId = data?.vendorId?._id;
          } else if (data?.paymentOf === "expense") {
            finalData.expenseTypeId = data?.expenseType?._id;
          } else if (data?.paymentOf === "invoice") {
            finalData.platformId = data?.platformId?._id;
            finalData.invoiceId = data?.invoiceId?._id;
          } else if (data?.paymentOf === "bill") {
            finalData.billId = data?.billId?._id;
            finalData.vendorId = data?.vendorId?._id;
          }

          if (data?.transactionType === "credit") {
            finalData.creditAmount = data?.amount;
          } else {
            finalData.debitAmount = data?.amount;
          }
          try {
            const response = await dispatch(
              updateEntryBook({ id: value?._id, data: finalData })
            ).unwrap();

            if (response?.status === 200) {
              enqueueSnackbar(response.data?.responseMessage, {
                variant: "success",
              });
              getData();
              onClose();
            } else {
              enqueueSnackbar(response.data?.responseMessage, {
                variant: "error",
              });
            }
          } catch (error) {
            enqueueSnackbar(error?.data?.responseMessage, {
              variant: "error",
            });
          }
          setSubmitting(false);
        }}
      >
        {({ isSubmitting, touched, errors, values, setFieldValue }) => (
          <Form>
            <Grid container spacing={3}>
              {(values.paymentOf === "party" ||
                values.paymentOf === "bill") && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    disabled
                    name="vendorId"
                    label="Vendor*"
                    value={values?.vendorId?.name}
                  />
                </Grid>
              )}
              {values.paymentOf === "invoice" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    disabled
                    name="platformId"
                    label="Platform*"
                    value={values?.platformId?.name}
                  />
                </Grid>
              )}
              {values.paymentOf === "invoice" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    disabled
                    name="invoiceId"
                    label="Invoice No*"
                    value={values?.invoiceId?.invoiceNumber}
                  />
                </Grid>
              )}
              {values.paymentOf === "bill" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    disabled
                    name="billId"
                    label="Bill No*"
                    value={values?.billId?.billNo}
                  />
                </Grid>
              )}
              {values.paymentOf === "expense" && (
                <Grid item md={6} xs={12}>
                  <JumboAutoComplete
                    options={expenseTypeData.list}
                    label="Expense Type*"
                    size="large"
                    value={values?.expenseType}
                    getOptionLabel={(option) => option.name}
                    name="expenseType"
                    onChange={(evt, value) => {
                      setFieldValue("expenseType", value);
                      setFieldValue("vendor", null);
                    }}
                    error={touched?.expenseType && Boolean(errors?.expenseType)}
                    helperText={touched?.expenseType && errors?.expenseType}
                  />
                </Grid>
              )}
              {values.paymentOf === "party" && (
                <Grid item md={6} xs={12}>
                  <JumboAutoComplete
                    options={incomeTypeData.list}
                    label="Income Type*"
                    size="large"
                    value={values?.incomeType}
                    getOptionLabel={(option) => option.name}
                    name="incomeType"
                    onChange={(evt, value) => {
                      setFieldValue("incomeType", value);
                    }}
                    error={touched?.incomeType && Boolean(errors?.incomeType)}
                    helperText={touched?.incomeType && errors?.incomeType}
                  />
                </Grid>
              )}
              <Grid item md={6} xs={12}>
                <JumboTextField
                  type="date"
                  label={"Transaction Date"}
                  name="transactionDate"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  value={values?.transactionDate}
                />
              </Grid>
              <Grid item md={6} sm={12}>
                <JumboAppTextField
                  fullWidth
                  type="number"
                  name="amount"
                  label="Amount*"
                />
              </Grid>
              <Grid item xs={12} sm={12} mb={2}>
                <JumboAppTextField fullWidth name="note" label="Note*" />
              </Grid>
            </Grid>
            <Stack
              spacing={{ xs: 1, sm: 2 }}
              direction="row"
              justifyContent="flex-end"
            >
              <LoadingButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                size="large"
                fullWidth={mobileView}
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                sx={{ mb: 3 }}
                loading={isSubmitting}
                loadingIndicator={<CircularProgress size={20} />}
                fullWidth={mobileView}
              >
                Save
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default EditEntryBook;
